/**
 * MVP module — shared Phase C query hooks (roster/hydration/subgroups).
 * Consumers: ExploreMvpsTab (roster builder, multispecialty gate),
 * assemble-inputs (multiSpecialty profile flag), Phase D subgroup context.
 */
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';
import { keepPreviousSameEntity } from '@/lib/query-keys';

export interface NpiProfileRow {
  npi: string;
  clinician_name: string | null;
  origin: 'qpp_file' | 'claims_detected';
  specialty_code: string | null;
  specialty_desc: string | null;
  org_count: number | null;
  mdcr_allowed_amt: number | null;
  mdcr_benes: number | null;
  dual_cnt: number | null;
  avg_risk_score: number | null;
  sources: Record<string, unknown> | null;
  fetched_at: string | null;
  match_tier?: string | null;
  org_matched?: boolean;
  suggested_org_name?: string | null;
  org_names?: string[];
  orgs?: Array<{
    name: string;
    individualEligible: boolean | null;
    groupEligible: boolean | null;
  }>;
  confirmed_org_name?: string | null;
  force_include?: boolean;
  type_description?: string | null;
  clinician_type?: string | null;
  mips_eligible_individual?: boolean | null;
  mips_eligible_group?: boolean | null;
  allowed_charges?: number | null;
  medicare_patients?: number | null;
  counts_for_practice?: boolean;
}

export interface NpiProfilesResponse {
  profiles: NpiProfileRow[];
  specialtyCodes: string[];
  multiSpecialty: boolean;
  countedNpis?: number;
  unmatchedNpis?: number;
  hydratedCount: number;
  lastHydrated: string | null;
  affiliationInsights?: {
    groupEligible: number;
    individualEligible: number;
  };
  practiceEligibility?: {
    mipsEligible: boolean | null;
    practiceName: string | null;
    meetsLvt: boolean | null;
    optInEligible: string | null;
    allowedCharges: number | null;
    medicarePatients: number | null;
    specialStatuses: unknown;
  } | null;
}

export interface Subgroup {
  id: number;
  practice_id: number;
  program_year: number;
  name: string;
  specialty_hint: string | null;
  members: string[];
}

export interface MvpSelection {
  id: number;
  mvp_id: string;
  entity_type: 'group' | 'subgroup';
  subgroup_id: number | null;
  subgroup_name?: string | null;
  registered: boolean;
}

/** Committed MVP selections (query key shared with ExploreMvpsTab). */
export function useMvpSelections(practiceId: number | string | undefined | null, year: number) {
  return useQuery<MvpSelection[]>({
    queryKey: ['mvp', 'selections', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/selections?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<MvpSelection[]>(2, String(practiceId ?? '')),
  });
}

export function useNpiProfiles(practiceId: number | string | undefined | null, year: number) {
  return useQuery<NpiProfilesResponse>({
    queryKey: ['mvp', 'npi-profiles', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/npi-profiles?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<NpiProfilesResponse>(2, String(practiceId ?? '')),
    staleTime: 5 * 60_000, // hydration cache changes monthly, not per-render
  });
}

export function useSubgroups(practiceId: number | string | undefined | null, year: number) {
  return useQuery<Subgroup[]>({
    queryKey: ['mvp', 'subgroups', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/subgroups?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<Subgroup[]>(2, String(practiceId ?? '')),
  });
}

export interface ResolvedFinancials {
  mode: 'hydrated' | 'qpp_only';
  tin: { fileValue: number | null; npiSum: number; resolved: number | null; source: string };
  npis: Array<{
    npi: string;
    clinicianName: string | null;
    clinicianType: string | null;
    specialty: string | null;
    inFile: boolean;
    orgMatched: boolean;
    matchTier: string | null;
    orgCount: number | null;
    fileValue: number | null;
    datasetValue: number | null;
    override: number | null;
    resolved: number | null;
    source: string;
  }>;
}

/** The ONE revenue resolution (spec §6 precedence chain) — all $ surfaces read this. */
export function useResolvedFinancials(practiceId: number | string | undefined | null, year: number) {
  return useQuery<ResolvedFinancials>({
    queryKey: ['mvp', 'financials', practiceId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/financials?year=${year}`),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<ResolvedFinancials>(2, String(practiceId ?? '')),
    staleTime: 5 * 60_000,
  });
}

export interface EligibilityPulseSummary {
  materialCount: number;
  individualEligFlips: number;
  groupEligFlips: number;
  statusFlips: number;
  revenueMoves: number;
  allowedChargesDelta: number;
}

export interface EligibilityPulseFeed {
  mode: string;
  summary?: EligibilityPulseSummary;
  materialTotal?: number;
  totalEvents?: number;
  lastSnapshotAt?: string | null;
  npisTracked?: number;
}

/** Material eligibility pulse (within-year default). */
export function useEligibilityPulse(
  practiceId: number | string | undefined | null,
  year: number,
  mode: 'within_year' | 'across_years' | 'client' = 'within_year',
) {
  return useQuery<EligibilityPulseFeed>({
    queryKey: ['mvp', 'eligibility-changes', practiceId ?? '', year, mode],
    queryFn: () =>
      apiClient.get(
        `/api/mvp/${practiceId}/eligibility-changes?year=${year}&mode=${mode}`,
      ),
    enabled: !!practiceId,
    placeholderData: keepPreviousSameEntity<EligibilityPulseFeed>(
      2,
      `${practiceId ?? ''}:${mode}`,
    ),
    staleTime: 60_000,
  });
}

/** Subgroup-derived quality summaries (same shape as /api/performance/.../summaries). */
export function useSubgroupSummaries(
  practiceId: number | string | undefined | null,
  subgroupId: number | null | undefined,
  year: number,
) {
  return useQuery<any[]>({
    queryKey: ['mvp', 'subgroup-summaries', practiceId ?? '', subgroupId ?? '', year],
    queryFn: () => apiClient.get(`/api/mvp/${practiceId}/subgroups/${subgroupId}/summaries?year=${year}`),
    enabled: !!practiceId && !!subgroupId,
    placeholderData: keepPreviousSameEntity<any[]>(2, `${practiceId ?? ''}:${subgroupId ?? ''}`),
  });
}
